import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { keyframes } from "@emotion/react";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import QrCodeScannerOutlinedIcon from "@mui/icons-material/QrCodeScannerOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import MuestrasMovimientoCamara from "../Muestras/MuestrasMovimientoCamara";
import {
  DESTINO_CLIENTE,
  emptyEnvioDestino,
  toMovimientoDestinoPayload,
} from "../Muestras/EnvioDestinoFields";
import {
  importMeetingSamples,
  leaveMeetingSamples,
  unleaveMeetingSamples,
} from "../../api/Meetings/meetingSessionsApi";
import MeetingUnleaveSamplesDialog from "./MeetingUnleaveSamplesDialog";

const motionSafe = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const rise = keyframes`
  from { opacity: 0; transform: translateY(10px) scale(0.96); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const pop = keyframes`
  0%, 8% { opacity: 0.2; transform: translateY(8px) scale(0.9); }
  28%, 62% { opacity: 1; transform: translateY(0) scale(1); }
  100% { opacity: 0.35; transform: translateY(4px) scale(0.95); }
`;

const slidePair = keyframes`
  0%, 12% { opacity: 0.25; transform: scale(0.86) translateY(4px); }
  35%, 60% { opacity: 1; transform: scale(1) translateY(0); }
  100% { opacity: 0.35; transform: scale(0.9) translateY(2px); }
`;

const scanSweep = keyframes`
  0% { transform: translateY(2px); opacity: 0.2; }
  45% { opacity: 1; }
  100% { transform: translateY(40px); opacity: 0.15; }
`;

const bob = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

/**
 * Prefill destino Cliente from meeting contacts / customerName.
 */
export function buildLeaveSamplesDestino(session) {
  const base = {
    ...emptyEnvioDestino(),
    TIPO_DESTINO: DESTINO_CLIENTE,
  };
  const contacts = Array.isArray(session?.clientContacts)
    ? session.clientContacts
    : [];
  const first = contacts.find((c) => Number(c?.customerId) > 0) || contacts[0];
  if (first) {
    const customerId = Number(first.customerId);
    const contactId = Number(first.contactId);
    if (Number.isInteger(customerId) && customerId > 0) {
      base.ID_CLIENTE = customerId;
    }
    if (Number.isInteger(contactId) && contactId > 0) {
      base.ID_CONTACTO = contactId;
    }
    const name = String(first.name || "").trim();
    if (name && !base.ID_CONTACTO) {
      base.PERSONA_ENVIO = name;
    }
  }
  if (!base.PERSONA_ENVIO && !base.ID_CONTACTO) {
    const fallback = String(session?.customerName || "").trim();
    if (fallback) base.PERSONA_ENVIO = fallback;
  }
  return base;
}

function Stage({ children, tint }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: { xs: 72, sm: 64 },
        overflow: "hidden",
        bgcolor: tint,
      }}
    >
      {children}
    </Box>
  );
}

function LeavePreview({ accent }) {
  return (
    <Stage tint={alpha(accent, 0.18)}>
      <Box
        sx={{
          position: "absolute",
          left: 12,
          top: 14,
          width: 40,
          height: 44,
          borderRadius: 1.25,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(accent, 0.45),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 6px 14px ${alpha(accent, 0.22)}`,
          animation: `${pop} 3.6s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <StorefrontOutlinedIcon sx={{ fontSize: 22, color: accent }} />
      </Box>
      <Box
        sx={{
          position: "absolute",
          right: 10,
          top: 16,
          px: 0.85,
          py: 0.45,
          borderRadius: 1.25,
          bgcolor: alpha(accent, 0.22),
          border: "1px solid",
          borderColor: alpha(accent, 0.55),
          animation: `${bob} 2.2s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <Typography
          sx={{ fontSize: "0.58rem", fontWeight: 900, color: accent }}
        >
          SE QUEDA
        </Typography>
      </Box>
      <Box
        sx={{
          position: "absolute",
          left: 8,
          right: 8,
          height: 2.5,
          borderRadius: 999,
          bgcolor: accent,
          boxShadow: `0 0 8px ${alpha(accent, 0.55)}`,
          animation: `${scanSweep} 1.9s ease-in-out infinite`,
          ...motionSafe,
        }}
      />
    </Stage>
  );
}

function FeedPreview({ accent }) {
  return (
    <Stage tint={alpha(accent, 0.16)}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            left: 10 + i * 24,
            top: 12 + (i % 2) * 5,
            width: 36,
            height: 44,
            borderRadius: 1.25,
            bgcolor: "#fff",
            border: "1.5px solid",
            borderColor: alpha(accent, 0.4),
            display: "grid",
            placeItems: "center",
            boxShadow: `0 6px 14px ${alpha(accent, 0.2)}`,
            animation: `${slidePair} 3.4s ease-in-out infinite`,
            animationDelay: `${i * 0.12}s`,
            ...motionSafe,
          }}
        >
          <PhotoLibraryOutlinedIcon sx={{ fontSize: 18, color: accent }} />
        </Box>
      ))}
    </Stage>
  );
}

function UnleavePreview({ accent }) {
  return (
    <Stage tint={alpha(accent, 0.18)}>
      <Box
        sx={{
          position: "absolute",
          left: 10,
          top: 14,
          width: 36,
          height: 40,
          borderRadius: 1.25,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(accent, 0.4),
          display: "grid",
          placeItems: "center",
          opacity: 0.45,
          ...motionSafe,
        }}
      >
        <StorefrontOutlinedIcon sx={{ fontSize: 18, color: accent }} />
      </Box>
      <Box
        sx={{
          position: "absolute",
          left: "42%",
          top: 18,
          width: 30,
          height: 30,
          borderRadius: "50%",
          bgcolor: alpha(accent, 0.18),
          border: "1.5px solid",
          borderColor: alpha(accent, 0.5),
          display: "grid",
          placeItems: "center",
          animation: `${bob} 1.8s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <UndoOutlinedIcon sx={{ fontSize: 16, color: accent }} />
      </Box>
      <Box
        sx={{
          position: "absolute",
          right: 10,
          top: 12,
          width: 40,
          height: 44,
          borderRadius: 1.25,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(accent, 0.45),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 6px 14px ${alpha(accent, 0.22)}`,
          animation: `${pop} 3.2s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <HomeOutlinedIcon sx={{ fontSize: 20, color: accent }} />
      </Box>
    </Stage>
  );
}

function IntentCard({
  active,
  preview: Preview,
  title,
  body,
  accent,
  onClick,
  delayMs = 0,
}) {
  const theme = useTheme();
  const muted = theme.palette.grey[400];
  const ink = active ? accent : muted;
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-pressed={active}
      sx={{
        p: 0,
        m: 0,
        width: "100%",
        minWidth: 0,
        textAlign: "left",
        cursor: "pointer",
        borderRadius: 2,
        overflow: "hidden",
        border: "2.5px solid",
        borderColor: active ? accent : alpha(theme.palette.text.primary, 0.12),
        bgcolor: active
          ? alpha(accent, 0.12)
          : alpha(theme.palette.grey[500], 0.06),
        boxShadow: active ? `0 10px 24px ${alpha(accent, 0.28)}` : "none",
        opacity: active ? 1 : 0.55,
        filter: active ? "none" : "grayscale(0.85)",
        transition:
          "border-color 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease, opacity 0.18s ease, filter 0.18s ease",
        font: "inherit",
        color: "inherit",
        animation: `${rise} 320ms ease both`,
        animationDelay: `${delayMs}ms`,
        WebkitTapHighlightColor: "transparent",
        touchAction: "manipulation",
        minHeight: { xs: 132, sm: 120 },
        ...motionSafe,
        "&:active": { transform: "scale(0.985)" },
        "&:hover": {
          opacity: 1,
          filter: "none",
          borderColor: active ? accent : alpha(accent, 0.55),
          bgcolor: alpha(accent, active ? 0.16 : 0.08),
        },
        "&:focus-visible": {
          outline: `2px solid ${accent}`,
          outlineOffset: 2,
        },
      }}
    >
      <Preview accent={ink} />
      <Box
        sx={{
          px: 1.15,
          py: 1.15,
          bgcolor: active
            ? alpha(accent, 0.1)
            : alpha(theme.palette.grey[500], 0.04),
          borderTop: "1px solid",
          borderColor: active
            ? alpha(accent, 0.22)
            : alpha(theme.palette.text.primary, 0.08),
        }}
      >
        <Stack direction="row" spacing={0.5} alignItems="center">
          {active ? (
            <CheckCircleRoundedIcon sx={{ fontSize: 18, color: accent }} />
          ) : null}
          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              lineHeight: 1.2,
              fontSize: { xs: "0.8rem", sm: "0.78rem" },
              color: ink,
            }}
          >
            {title}
          </Typography>
        </Stack>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 0.4,
            lineHeight: 1.35,
            fontSize: { xs: "0.72rem", sm: "0.68rem" },
            color: active
              ? alpha(theme.palette.text.primary, 0.65)
              : alpha(theme.palette.text.primary, 0.4),
          }}
        >
          {body}
        </Typography>
      </Box>
    </Box>
  );
}

export default function MeetingLeaveSamplesDialog({
  open,
  onClose,
  session,
  onDone,
  allowRecap = false,
  onCreateRecap,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const sessionId = Number(session?.id);

  const [intent, setIntent] = useState("leave");
  const [step, setStep] = useState("scan");
  const [lastResult, setLastResult] = useState(null);
  const [pendingUnleave, setPendingUnleave] = useState(null);
  const [unleaveBusy, setUnleaveBusy] = useState(false);
  const [unleaveError, setUnleaveError] = useState("");
  const unleaveResolverRef = useRef(null);
  /** "done" = pantalla Listo; "refresh" = solo actualiza sesión. */
  const unleaveFinishModeRef = useRef("refresh");

  const leftWithClient = useMemo(
    () =>
      Array.isArray(session?.leftWithClient) ? session.leftWithClient : [],
    [session?.leftWithClient],
  );

  useEffect(() => {
    if (!open) {
      setIntent("leave");
      setStep("scan");
      setLastResult(null);
      setPendingUnleave(null);
      setUnleaveBusy(false);
      setUnleaveError("");
      if (unleaveResolverRef.current) {
        unleaveResolverRef.current(null);
        unleaveResolverRef.current = null;
      }
    }
  }, [open]);

  const initialDestino = useMemo(
    () => buildLeaveSamplesDestino(session),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      session?.id,
      session?.customerName,
      JSON.stringify(
        (session?.clientContacts || []).map((c) => [
          c.customerId,
          c.contactId,
          c.name,
        ]),
      ),
    ],
  );

  const isLeave = intent === "leave";
  const isUnleave = intent === "unleave";
  /** Morado = se queda / Teal = meter en reunión / Naranja = deshacer. */
  const leaveAccent = "#6a1b9a";
  const feedAccent = theme.palette.primary.main || "#00a896";
  const unleaveAccent = theme.palette.warning.dark || "#e65100";

  const askUnleaveConfirm = useCallback((samples) => {
    return new Promise((resolve) => {
      unleaveResolverRef.current = resolve;
      setPendingUnleave(samples);
    });
  }, []);

  const executeUnleave = useCallback(
    async (samples) => {
      setUnleaveError("");
      const list = (samples || [])
        .map((s) => ({
          idMuestra: Number(s?.idMuestra ?? s?.ID_MUESTRA ?? s?.id),
          muestraNombre: String(s?.muestraNombre ?? s?.MUESTRA ?? "").trim(),
        }))
        .filter((s) => Number.isInteger(s.idMuestra) && s.idMuestra > 0);
      if (!list.length) {
        return {
          okCount: 0,
          failedIds: [],
          cancelled: true,
          mode: "unleave",
        };
      }
      const confirmed = await askUnleaveConfirm(list);
      if (!confirmed) {
        return {
          okCount: 0,
          failedIds: [],
          cancelled: true,
          mode: "unleave",
        };
      }
      setUnleaveBusy(true);
      try {
        const data = await unleaveMeetingSamples(sessionId, {
          idMuestras: list.map((s) => s.idMuestra),
        });
        const unleaveResult = data?.unleaveResult || {};
        const failedItems = Array.isArray(unleaveResult.failedItems)
          ? unleaveResult.failedItems
          : [];
        const failedIds = new Set(
          failedItems.map((f) => Number(f.idMuestra)).filter(Boolean),
        );
        const okCount = Number(
          unleaveResult.ok ?? list.length - failedIds.size,
        );
        if (!okCount) {
          const firstErr =
            failedItems[0]?.error || t("Meetings.leaveSamplesUndoError");
          setUnleaveError(firstErr);
        }
        return {
          okCount,
          failedIds: [...failedIds],
          successMessage: t("Meetings.leaveSamplesUndoSuccess", {
            count: okCount,
          }),
          errorMessage: t("Meetings.leaveSamplesUndoError"),
          session: data,
          photoIds: [],
          mode: "unleave",
          unleaveResult,
        };
      } catch (err) {
        const msg =
          err?.response?.data?.error ||
          err?.message ||
          t("Meetings.leaveSamplesUndoError");
        setUnleaveError(msg);
        throw err;
      } finally {
        setUnleaveBusy(false);
        setPendingUnleave(null);
      }
    },
    [askUnleaveConfirm, sessionId, t],
  );

  const customRegister = useCallback(
    async ({ items, destino, numCourier, setProgress }) => {
      setProgress?.(0);
      const samples = items.map((item) => ({
        idMuestra: item.ID_MUESTRA,
        muestraNombre: item.MUESTRA,
      }));

      if (intent === "unleave") {
        const result = await executeUnleave(samples);
        setProgress?.(items.length);
        return result;
      }

      if (intent === "ungrouped") {
        const data = await importMeetingSamples(sessionId, { samples });
        setProgress?.(items.length);
        const importResult = data?.importResult || {};
        const failedItems = Array.isArray(importResult.failedItems)
          ? importResult.failedItems
          : [];
        const failedIds = new Set(
          failedItems.map((f) => Number(f.idMuestra)).filter(Boolean),
        );
        const okCount = Number(
          importResult.ok ?? items.length - failedIds.size,
        );
        const photoIds = (importResult.added || [])
          .map((a) => Number(a.photoId))
          .filter((n) => Number.isInteger(n) && n > 0);

        return {
          okCount,
          failedIds: [...failedIds],
          successMessage: t("Meetings.importSamplesSuccess", {
            count: okCount,
          }),
          errorMessage: t("Meetings.importSamplesError"),
          session: data,
          photoIds,
          mode: "ungrouped",
          importResult,
        };
      }

      const alreadyLeft = new Set(
        leftWithClient
          .map((x) => Number(x?.idMuestra))
          .filter((n) => Number.isInteger(n) && n > 0),
      );
      const toUnleave = samples.filter((s) =>
        alreadyLeft.has(Number(s.idMuestra)),
      );
      const toLeave = samples.filter(
        (s) => !alreadyLeft.has(Number(s.idMuestra)),
      );

      // Solo ya dejadas en modo leave → deshacer (con confirm)
      if (toUnleave.length && !toLeave.length) {
        const result = await executeUnleave(toUnleave);
        setProgress?.(items.length);
        return result;
      }

      // Mezcla: deshacer las ya dejadas (con confirm) + dejar las nuevas
      let sessionData = null;
      let leaveResult = {};
      let unleaveResult = null;
      const failedIds = new Set();

      if (toUnleave.length) {
        const undoResult = await executeUnleave(toUnleave);
        if (undoResult?.cancelled) {
          for (const s of toUnleave) failedIds.add(Number(s.idMuestra));
        } else {
          sessionData = undoResult?.session || null;
          unleaveResult = undoResult?.unleaveResult || null;
          for (const fid of undoResult?.failedIds || []) {
            if (fid) failedIds.add(Number(fid));
          }
        }
      }

      if (toLeave.length) {
        const destinoPayload = toMovimientoDestinoPayload(destino);
        const data = await leaveMeetingSamples(sessionId, {
          samples: toLeave,
          ID_CLIENTE: destinoPayload.ID_CLIENTE,
          ID_CONTACTO: destinoPayload.ID_CONTACTO,
          PERSONA_ENVIO: destinoPayload.PERSONA_ENVIO,
          NUM_COURIER: numCourier || null,
          FECHA_ENVIO: dayjs().format("YYYY-MM-DD"),
        });
        sessionData = data;
        leaveResult = data?.leaveResult || {};
        for (const f of leaveResult.failedItems || []) {
          const fid = Number(f.idMuestra);
          if (fid) failedIds.add(fid);
        }
      }

      setProgress?.(items.length);

      const leaveOk = Number(leaveResult.ok || 0);
      const undoOk = Number(unleaveResult?.ok || 0);
      const okCount = leaveOk + undoOk;
      const photoIds = (leaveResult.added || [])
        .map((a) => Number(a.photoId))
        .filter((n) => Number.isInteger(n) && n > 0);

      let successMessage = t("Meetings.leaveSamplesSuccess", {
        count: leaveOk || okCount,
      });
      if (undoOk && !leaveOk) {
        successMessage = t("Meetings.leaveSamplesUndoSuccess", {
          count: undoOk,
        });
      } else if (undoOk && leaveOk) {
        successMessage = `${t("Meetings.leaveSamplesSuccess", {
          count: leaveOk,
        })} · ${t("Meetings.leaveSamplesUndoSuccess", { count: undoOk })}`;
      }

      return {
        okCount,
        failedIds: [...failedIds],
        successMessage,
        errorMessage: t("Meetings.leaveSamplesError"),
        session: sessionData,
        photoIds,
        mode: leaveOk ? "leave" : "unleave",
        leaveResult,
        unleaveResult,
      };
    },
    [executeUnleave, intent, leftWithClient, sessionId, t],
  );

  const resolveUnleaveConfirm = useCallback((ok) => {
    const resolve = unleaveResolverRef.current;
    unleaveResolverRef.current = null;
    setPendingUnleave(null);
    if (typeof resolve === "function") resolve(Boolean(ok));
  }, []);

  const finishAndClose = useCallback(
    async (result) => {
      if (result?.session && typeof onDone === "function") {
        await onDone(result.session);
      }
      onClose?.();
    },
    [onClose, onDone],
  );

  const handleRegistered = useCallback(
    async (result) => {
      if (result?.session && typeof onDone === "function") {
        await onDone(result.session);
      }
      setLastResult(result || null);
      setStep("done");
    },
    [onDone],
  );

  /** Abre confirmación (sin Promise): el API se lanza al pulsar Sí en el diálogo. */
  const openUnleaveConfirm = useCallback(
    (samples, { finishToDone = false } = {}) => {
      const list = (samples || [])
        .map((s) => ({
          idMuestra: Number(s?.idMuestra ?? s?.ID_MUESTRA ?? s?.id),
          muestraNombre: String(
            s?.muestraNombreCliente || s?.muestraNombre || s?.MUESTRA || "",
          ).trim(),
        }))
        .filter((s) => Number.isInteger(s.idMuestra) && s.idMuestra > 0);
      if (!list.length || unleaveBusy) return;
      setUnleaveError("");
      unleaveResolverRef.current = null;
      unleaveFinishModeRef.current = finishToDone ? "done" : "refresh";
      setPendingUnleave(list);
    },
    [unleaveBusy],
  );

  const handleUnleaveAllListed = useCallback(() => {
    // Cierra el diálogo tras deshacer; no pantalla Done/Recap
    openUnleaveConfirm(leftWithClient, { finishToDone: false });
  }, [leftWithClient, openUnleaveConfirm]);

  const handleUnleaveOneListed = useCallback(
    (item) => {
      openUnleaveConfirm([item], { finishToDone: false });
    },
    [openUnleaveConfirm],
  );

  const confirmPendingUnleave = useCallback(async () => {
    const list = pendingUnleave || [];
    if (!list.length) return;
    // Si hay Promise (flujo escaneo), solo resolvemos y el await sigue en executeUnleave
    if (typeof unleaveResolverRef.current === "function") {
      resolveUnleaveConfirm(true);
      return;
    }
    setUnleaveBusy(true);
    setUnleaveError("");
    try {
      const data = await unleaveMeetingSamples(sessionId, {
        idMuestras: list.map((s) => s.idMuestra),
      });
      const unleaveResult = data?.unleaveResult || {};
      const okCount = Number(unleaveResult.ok || 0);
      const failedItems = Array.isArray(unleaveResult.failedItems)
        ? unleaveResult.failedItems
        : [];
      setPendingUnleave(null);
      if (!okCount) {
        setUnleaveError(
          failedItems[0]?.error || t("Meetings.leaveSamplesUndoError"),
        );
        return;
      }
      const result = {
        okCount,
        failedIds: failedItems.map((f) => Number(f.idMuestra)).filter(Boolean),
        successMessage: t("Meetings.leaveSamplesUndoSuccess", {
          count: okCount,
        }),
        session: data,
        photoIds: [],
        mode: "unleave",
        unleaveResult,
      };
      if (unleaveFinishModeRef.current === "done") {
        await handleRegistered(result);
      } else {
        if (typeof onDone === "function") await onDone(data);
        onClose?.();
      }
    } catch (err) {
      setUnleaveError(
        err?.response?.data?.error ||
          err?.message ||
          t("Meetings.leaveSamplesUndoError"),
      );
    } finally {
      setUnleaveBusy(false);
    }
  }, [
    handleRegistered,
    onClose,
    onDone,
    pendingUnleave,
    resolveUnleaveConfirm,
    sessionId,
    t,
  ]);

  const handleCreateRecap = useCallback(async () => {
    const photoIds = Array.isArray(lastResult?.photoIds)
      ? lastResult.photoIds
      : [];
    if (typeof onCreateRecap === "function") {
      await onCreateRecap({
        photoIds,
        session: lastResult?.session || session,
      });
    }
    onClose?.();
  }, [lastResult, onClose, onCreateRecap, session]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        fullScreen={isNarrow}
        transitionDuration={160}
        sx={{ zIndex: (th) => th.zIndex.modal + 2 }}
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: "blur(10px)",
              backgroundColor: "rgba(15, 23, 42, 0.45)",
            },
          },
        }}
        disableEnforceFocus={Boolean(pendingUnleave?.length)}
        PaperProps={{
          sx: {
            borderRadius: isNarrow ? 0 : 3,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
            maxHeight: isNarrow ? "100%" : "92vh",
          },
        }}
      >
        <Box
          sx={{
            px: 1.75,
            pt: 1.5,
            pb: 1.15,
            flexShrink: 0,
            borderBottom: "1px solid",
            borderColor: "divider",
            background: (th) =>
              `linear-gradient(135deg, ${alpha(th.palette.secondary.main, 0.1)} 0%, ${alpha(
                th.palette.primary.main,
                0.05,
              )} 100%)`,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ minWidth: 0 }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: alpha(theme.palette.secondary.main, 0.16),
                  color: "secondary.main",
                  flexShrink: 0,
                }}
              >
                <QrCodeScannerOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 800, lineHeight: 1.15 }}
                >
                  {step === "done"
                    ? t("Meetings.sampleScanDoneTitle")
                    : t("Meetings.sampleScanTitle")}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", lineHeight: 1.3 }}
                >
                  {step === "done"
                    ? t("Meetings.sampleScanDoneBody", {
                        count: Number(lastResult?.okCount || 0),
                      })
                    : t("Meetings.sampleScanBodyShort")}
                </Typography>
              </Box>
            </Stack>
            <IconButton
              aria-label={t("Meetings.cancel")}
              onClick={onClose}
              size="small"
            >
              <CloseOutlinedIcon />
            </IconButton>
          </Stack>
        </Box>

        <Box
          sx={{
            px: { xs: 1.5, sm: 1.75 },
            pt: { xs: 1.5, sm: 1.35 },
            pb: {
              xs: "max(28px, calc(16px + env(safe-area-inset-bottom, 0px)))",
              sm: 3,
            },
            overflow: "auto",
            flex: 1,
            minHeight: 0,
            WebkitOverflowScrolling: "touch",
          }}
        >
          {step === "scan" ? (
            <Stack spacing={{ xs: 1.5, sm: 1.35 }} sx={{ width: "100%" }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: leftWithClient.length
                    ? { xs: "1fr", sm: "1fr 1fr 1fr" }
                    : "1fr 1fr",
                  gap: { xs: 1.1, sm: 1 },
                  width: "100%",
                }}
              >
                <IntentCard
                  active={isLeave}
                  preview={LeavePreview}
                  title={t("Meetings.sampleScanIntentLeave")}
                  body={t("Meetings.sampleScanIntentLeaveHint")}
                  accent={leaveAccent}
                  onClick={() => setIntent("leave")}
                  delayMs={0}
                />
                <IntentCard
                  active={intent === "ungrouped"}
                  preview={FeedPreview}
                  title={t("Meetings.sampleScanIntentFeed")}
                  body={t("Meetings.sampleScanIntentFeedHint")}
                  accent={feedAccent}
                  onClick={() => setIntent("ungrouped")}
                  delayMs={60}
                />
                {leftWithClient.length ? (
                  <IntentCard
                    active={isUnleave}
                    preview={UnleavePreview}
                    title={t("Meetings.sampleScanIntentUnleave")}
                    body={t("Meetings.sampleScanIntentUnleaveHint")}
                    accent={unleaveAccent}
                    onClick={() => setIntent("unleave")}
                    delayMs={100}
                  />
                ) : null}
              </Box>

              {isUnleave && leftWithClient.length ? (
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: alpha(unleaveAccent, 0.35),
                    bgcolor: alpha(unleaveAccent, 0.08),
                    animation: `${rise} 240ms ease both`,
                    ...motionSafe,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 800,
                      color: unleaveAccent,
                      display: "block",
                      mb: 0.5,
                    }}
                  >
                    {t("Meetings.leaveSamplesListTitle")} ·{" "}
                    {leftWithClient.length}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 1, lineHeight: 1.4 }}
                  >
                    {t("Meetings.leaveSamplesUndoAllHint")}
                  </Typography>
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    useFlexGap
                    spacing={0.6}
                    sx={{ mb: 1.15 }}
                  >
                    {leftWithClient.map((item) => (
                      <Chip
                        key={item.idMuestra}
                        size="small"
                        variant="outlined"
                        label={
                          item.muestraNombreCliente ||
                          item.muestraNombre ||
                          `#${item.idMuestra}`
                        }
                        onDelete={
                          unleaveBusy
                            ? undefined
                            : () => handleUnleaveOneListed(item)
                        }
                        sx={{
                          fontWeight: 700,
                          borderColor: alpha(unleaveAccent, 0.45),
                          color: unleaveAccent,
                        }}
                      />
                    ))}
                  </Stack>
                  <Button
                    fullWidth
                    variant="contained"
                    disabled={unleaveBusy || !leftWithClient.length}
                    onClick={handleUnleaveAllListed}
                    startIcon={
                      unleaveBusy ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <UndoOutlinedIcon />
                      )
                    }
                    sx={{
                      fontWeight: 800,
                      textTransform: "none",
                      minHeight: 48,
                      bgcolor: unleaveAccent,
                      boxShadow: `0 8px 18px ${alpha(unleaveAccent, 0.28)}`,
                      "&:hover": { bgcolor: "#bf360c" },
                    }}
                  >
                    {t("Meetings.leaveSamplesUndoAll", {
                      count: leftWithClient.length,
                    })}
                  </Button>
                  {unleaveError ? (
                    <Alert
                      severity="error"
                      sx={{ mt: 1 }}
                      onClose={() => setUnleaveError("")}
                    >
                      {unleaveError}
                    </Alert>
                  ) : null}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 1, lineHeight: 1.35 }}
                  >
                    {t("Meetings.leaveSamplesUndoScanHint")}
                  </Typography>
                </Box>
              ) : null}

              {open &&
              Number.isInteger(sessionId) &&
              sessionId > 0 &&
              !isUnleave ? (
                <Box
                  sx={{
                    width: "100%",
                    animation: `${rise} 280ms ease both`,
                    animationDelay: "80ms",
                    ...motionSafe,
                  }}
                >
                  <MuestrasMovimientoCamara
                    key={intent}
                    embedded
                    compact
                    title=""
                    confirmLabel={
                      isLeave
                        ? t("Meetings.leaveSamplesConfirm")
                        : t("Meetings.importSamplesConfirm")
                    }
                    initialDestino={initialDestino}
                    allowedTipos={[DESTINO_CLIENTE]}
                    hideCourier
                    preferGunMode={!isNarrow}
                    selectionOnly={!isLeave}
                    customRegister={customRegister}
                    onRegistered={handleRegistered}
                  />
                </Box>
              ) : null}

              {open &&
              Number.isInteger(sessionId) &&
              sessionId > 0 &&
              isUnleave ? (
                <Box
                  sx={{
                    width: "100%",
                    animation: `${rise} 280ms ease both`,
                    animationDelay: "80ms",
                    ...motionSafe,
                  }}
                >
                  <MuestrasMovimientoCamara
                    key={`${intent}-scan`}
                    embedded
                    compact
                    title=""
                    confirmLabel={t("Meetings.leaveSamplesUndoConfirmScan")}
                    initialDestino={initialDestino}
                    allowedTipos={[DESTINO_CLIENTE]}
                    hideCourier
                    preferGunMode={!isNarrow}
                    selectionOnly
                    customRegister={customRegister}
                    onRegistered={handleRegistered}
                  />
                </Box>
              ) : null}
            </Stack>
          ) : (
            <Stack
              spacing={1.35}
              sx={{
                animation: `${rise} 240ms ease both`,
                ...motionSafe,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleRoundedIcon color="success" sx={{ fontSize: 26 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  {lastResult?.mode === "unleave"
                    ? t("Meetings.leaveSamplesUndoSuccess", {
                        count: Number(lastResult?.okCount || 0),
                      })
                    : t("Meetings.sampleScanDoneOk", {
                        count: Number(lastResult?.okCount || 0),
                      })}
                </Typography>
              </Stack>

              {allowRecap &&
              lastResult?.mode !== "unleave" &&
              Number(lastResult?.okCount || 0) > 0 ? (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: alpha(theme.palette.info.main, 0.35),
                    bgcolor: alpha(theme.palette.info.main, 0.08),
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    {t("Meetings.sampleScanRecapAsk")}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.35, lineHeight: 1.4 }}
                  >
                    {t("Meetings.sampleScanRecapAskHint")}
                  </Typography>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    sx={{ mt: 1.25 }}
                  >
                    <Button
                      variant="contained"
                      color="info"
                      onClick={handleCreateRecap}
                      startIcon={<SummarizeOutlinedIcon />}
                      sx={{
                        fontWeight: 800,
                        textTransform: "none",
                        minHeight: 44,
                      }}
                    >
                      {t("Meetings.sampleScanRecapYes")}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => finishAndClose(lastResult)}
                      sx={{
                        fontWeight: 700,
                        textTransform: "none",
                        minHeight: 44,
                      }}
                    >
                      {t("Meetings.sampleScanRecapNo")}
                    </Button>
                  </Stack>
                </Box>
              ) : (
                <Button
                  variant="contained"
                  onClick={() => finishAndClose(lastResult)}
                  sx={{
                    alignSelf: "stretch",
                    fontWeight: 800,
                    textTransform: "none",
                    minHeight: 44,
                  }}
                >
                  {t("Meetings.sampleScanClose")}
                </Button>
              )}

              {lastResult?.mode !== "unleave" ? (
                <Button
                  variant="text"
                  onClick={() => {
                    setStep("scan");
                    setLastResult(null);
                  }}
                  sx={{
                    alignSelf: "flex-start",
                    fontWeight: 700,
                    textTransform: "none",
                  }}
                >
                  {t("Meetings.sampleScanMore")}
                </Button>
              ) : null}
            </Stack>
          )}
        </Box>
      </Dialog>

      {typeof document !== "undefined"
        ? createPortal(
            <MeetingUnleaveSamplesDialog
              open={Boolean(pendingUnleave?.length)}
              onClose={() => {
                if (unleaveBusy) return;
                if (typeof unleaveResolverRef.current === "function") {
                  resolveUnleaveConfirm(false);
                } else {
                  setPendingUnleave(null);
                }
              }}
              onConfirm={confirmPendingUnleave}
              busy={unleaveBusy}
              count={pendingUnleave?.length || 1}
              sampleName={
                pendingUnleave?.length === 1
                  ? pendingUnleave[0]?.muestraNombre || ""
                  : ""
              }
            />,
            document.body,
          )
        : null}
    </>
  );
}
